// src/components/SmartFlow/JSONTreeView.jsx
import React, { useState } from "react";

/**
 * JSONTreeView - Visualizador de JSON em árvore (estilo n8n)
 * Suporta drag-and-drop de propriedades
 */
const JSONTreeView = ({ data, path = "$json", onPropertyDragStart }) => {
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (key) => {
    setExpanded((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const renderValue = (value, currentPath, key) => {
    const fullPath = key ? `${currentPath}.${key}` : currentPath;

    // null ou undefined
    if (value === null || value === undefined) {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span
            draggable
            onDragStart={(e) => onPropertyDragStart && onPropertyDragStart(e, fullPath, value)}
            style={{
              color: "#95a5a6",
              fontStyle: "italic",
              cursor: "grab",
              padding: "2px 6px",
              borderRadius: "3px",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#ecf0f1")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            {value === null ? "null" : "undefined"}
          </span>
        </div>
      );
    }

    // boolean
    if (typeof value === "boolean") {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span
            draggable
            onDragStart={(e) => onPropertyDragStart && onPropertyDragStart(e, fullPath, value)}
            style={{
              color: value ? "#27ae60" : "#e74c3c",
              fontWeight: "bold",
              cursor: "grab",
              padding: "2px 6px",
              borderRadius: "3px",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#ecf0f1")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            {value.toString()}
          </span>
        </div>
      );
    }

    // number
    if (typeof value === "number") {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span
            draggable
            onDragStart={(e) => onPropertyDragStart && onPropertyDragStart(e, fullPath, value)}
            style={{
              color: "#3498db",
              cursor: "grab",
              padding: "2px 6px",
              borderRadius: "3px",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#ecf0f1")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            {value}
          </span>
        </div>
      );
    }

    // string
    if (typeof value === "string") {
      return (
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span
            draggable
            onDragStart={(e) => onPropertyDragStart && onPropertyDragStart(e, fullPath, value)}
            style={{
              color: "#e67e22",
              cursor: "grab",
              padding: "2px 6px",
              borderRadius: "3px",
              wordBreak: "break-word",
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = "#ecf0f1")}
            onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
          >
            "{value.length > 100 ? value.substring(0, 100) + "..." : value}"
          </span>
        </div>
      );
    }

    // array
    if (Array.isArray(value)) {
      const isExpanded = expanded[fullPath];
      return (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              padding: "2px 0",
            }}
            onClick={() => toggleExpand(fullPath)}
          >
            <span style={{ fontSize: "12px", color: "#7f8c8d", width: "16px" }}>
              {isExpanded ? "▼" : "▶"}
            </span>
            <span
              draggable
              onDragStart={(e) => {
                e.stopPropagation();
                onPropertyDragStart && onPropertyDragStart(e, fullPath, value);
              }}
              style={{
                color: "#9b59b6",
                fontWeight: "bold",
                cursor: "grab",
                padding: "2px 6px",
                borderRadius: "3px",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#ecf0f1")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              [ {value.length} items ]
            </span>
          </div>
          {isExpanded && (
            <div style={{ marginLeft: "24px", borderLeft: "1px solid #ecf0f1", paddingLeft: "12px" }}>
              {value.map((item, index) => (
                <div key={index} style={{ marginBottom: "4px" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <span style={{ color: "#7f8c8d", fontSize: "12px", minWidth: "30px" }}>
                      [{index}]:
                    </span>
                    {renderValue(item, `${fullPath}[${index}]`, null)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    // object
    if (typeof value === "object") {
      const isExpanded = expanded[fullPath];
      const keys = Object.keys(value);

      return (
        <div>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              padding: "2px 0",
            }}
            onClick={() => toggleExpand(fullPath)}
          >
            <span style={{ fontSize: "12px", color: "#7f8c8d", width: "16px" }}>
              {isExpanded ? "▼" : "▶"}
            </span>
            <span
              draggable
              onDragStart={(e) => {
                e.stopPropagation();
                onPropertyDragStart && onPropertyDragStart(e, fullPath, value);
              }}
              style={{
                color: "#8e44ad",
                fontWeight: "bold",
                cursor: "grab",
                padding: "2px 6px",
                borderRadius: "3px",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "#ecf0f1")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
            >
              {"{" + keys.length + " keys}"}
            </span>
          </div>
          {isExpanded && (
            <div style={{ marginLeft: "24px", borderLeft: "1px solid #ecf0f1", paddingLeft: "12px" }}>
              {keys.map((objKey) => (
                <div key={objKey} style={{ marginBottom: "4px" }}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
                    <span
                      style={{
                        color: "#34495e",
                        fontSize: "13px",
                        fontWeight: "500",
                        minWidth: "80px",
                        wordBreak: "break-word",
                      }}
                    >
                      {objKey}:
                    </span>
                    {renderValue(value[objKey], fullPath, objKey)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return <span style={{ color: "#7f8c8d" }}>Unknown type</span>;
  };

  if (!data || (typeof data === "object" && Object.keys(data).length === 0)) {
    return (
      <div
        style={{
          padding: "40px 20px",
          textAlign: "center",
          color: "#95a5a6",
          fontSize: "14px",
          fontStyle: "italic",
        }}
      >
        No data available
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "12px",
        fontSize: "13px",
        fontFamily: "Monaco, Menlo, monospace",
        overflowX: "auto",
      }}
    >
      {renderValue(data, path, null)}
    </div>
  );
};

export default JSONTreeView;
